import React from 'react';
import { Form, Input, DatePicker, InputNumber, Row, Col } from 'antd';
import dayjs from 'dayjs';
import LookupField from '../../../components/LookupField';
import { T202FormData } from '../hooks/useT202Record';

const { TextArea } = Input;

interface Props {
  formData: T202FormData;
  isEditing: boolean;
  setField: <K extends keyof T202FormData>(field: K, value: T202FormData[K]) => void;
  employeeName: string;
  departmentName: string;
  onEmployeeConfirm: (code: string) => void;
  onDepartmentConfirm: (code: string) => void;
}

const Tab1BasicInfo: React.FC<Props> = React.memo(({ formData, isEditing, setField, employeeName, departmentName, onEmployeeConfirm, onDepartmentConfirm }) => {
  const toDate = (val: string) => (val ? dayjs(val) : null);
  const fromDate = (d: dayjs.Dayjs | null) => (d ? d.format('YYYY-MM-DD') : '');

  return (
    <Form layout="vertical" style={{ paddingTop: 8 }}>
      <Row gutter={16}>
        {/* 公告主題 */}
        <Col xs={24} sm={16}>
          <Form.Item label="主題" required>
            <Input
              value={formData.ft120_21}
              onChange={e => setField('ft120_21', e.target.value)}
              disabled={!isEditing}
              maxLength={60}
              placeholder="請輸入公告主題"
            />
          </Form.Item>
        </Col>

        {/* 分類碼 */}
        <Col xs={24} sm={8}>
          <Form.Item label="分類碼">
            <InputNumber
              value={formData.ft120_16}
              onChange={v => setField('ft120_16', v ?? 0)}
              disabled={!isEditing}
              style={{ width: '100%' }}
              min={0}
              precision={0}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        {/* 申請人 (LookupInput: employee) */}
        <Col xs={24} sm={8}>
          <Form.Item label="申請人">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LookupField
                lookupType="employee"
                value={formData.ft120_13}
                onChange={v => setField('ft120_13', v)}
                onCodeConfirm={onEmployeeConfirm}
                disabled={!isEditing}
                maxLength={8}
                style={{ width: 120 }}
              />
              <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 13 }}>{employeeName || '--'}</span>
            </div>
          </Form.Item>
        </Col>

        {/* 申請部門 (LookupInput: department) */}
        <Col xs={24} sm={8}>
          <Form.Item label="申請部門">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LookupField
                lookupType="department"
                value={formData.ft120_14}
                onChange={v => setField('ft120_14', v)}
                onCodeConfirm={onDepartmentConfirm}
                disabled={!isEditing}
                maxLength={4}
                style={{ width: 120 }}
              />
              <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 13 }}>{departmentName || '--'}</span>
            </div>
          </Form.Item>
        </Col>

        {/* ft120_15 moved to form-level header */}
      </Row>

      <Row gutter={16}>
        {/* 公告開始日 */}
        <Col xs={24} sm={12}>
          <Form.Item label="公告開始日">
            <DatePicker
              value={toDate(formData.ft120_11)}
              onChange={d => setField('ft120_11', fromDate(d))}
              disabled={!isEditing}
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
            />
          </Form.Item>
        </Col>

        {/* 公告結束日 */}
        <Col xs={24} sm={12}>
          <Form.Item label="公告結束日">
            <DatePicker
              value={toDate(formData.ft120_12)}
              onChange={d => setField('ft120_12', fromDate(d))}
              disabled={!isEditing}
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
            />
          </Form.Item>
        </Col>
      </Row>

      {/* 附加欄位 */}
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item label="附加欄位">
            <Input
              value={formData.ft120_20}
              onChange={e => setField('ft120_20', e.target.value)}
              disabled={!isEditing}
              maxLength={10}
              placeholder="附加欄位"
            />
          </Form.Item>
        </Col>
      </Row>

      {/* 公告內容 */}
      <Form.Item label="內容">
        <TextArea
          value={formData.ft120_22}
          onChange={e => setField('ft120_22', e.target.value)}
          disabled={!isEditing}
          rows={6}
          maxLength={2000}
          placeholder="請輸入公告內容..."
          showCount={isEditing}
        />
      </Form.Item>
    </Form>
  );
});

Tab1BasicInfo.displayName = 'Tab1BasicInfo';

export default Tab1BasicInfo;
